import { Outlet } from "react-router-dom";
import { motion } from "framer-motion";
import { MaskedLines, Reveal } from "@/components/site/Primitives";
import { Header } from "@/components/site/Header";
import { SiteFooter } from "@/components/site/SiteFooter";
import { NoWidows } from "@/components/site/NoWidows";

export const Layout = () => (
  <>
    <NoWidows />
    <Header />
    <main>
      <Outlet />
    </main>
    <SiteFooter />
  </>
);

export const BannerHero = ({ title, image, alt, eyebrow, intro, caption, testId }) => (
  <section data-testid={testId}>
    <div className="relative mt-[116px] h-[340px] overflow-hidden md:h-[530px]">
      <motion.img src={image} alt={alt} initial={{ scale: 1.08 }} animate={{ scale: 1 }} transition={{ duration: 1.8, ease: [0.16, 1, 0.3, 1] }} className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#001870]/70 via-[#001870]/25 to-transparent" />
      <div className="relative mx-auto max-w-[1400px] px-5 pt-14 text-white md:px-10 md:pt-20">
        {eyebrow && <p className="text-xs font-semibold uppercase tracking-[0.28em] text-[#E3BC6E] md:text-sm">{eyebrow}</p>}
        <h1 className="font-display mt-3 text-5xl font-bold uppercase leading-[0.9] drop-shadow-[0_3px_12px_rgba(0,0,0,0.45)] sm:text-6xl lg:text-7xl"><MaskedLines lines={[title]} /></h1>
      </div>
      {caption && <p className="absolute bottom-3 right-5 font-mono text-[11px] uppercase tracking-[0.12em] text-white/85 md:right-10">{caption}</p>}
    </div>
    <div className="section-light border-b border-slate-200">
      <Reveal className="mx-auto max-w-5xl px-5 py-14 text-center md:py-20">
        <p data-testid={testId ? `${testId}-intro` : undefined} className="font-display text-2xl font-medium leading-snug text-[#33415f] md:text-[1.85rem]">{intro}</p>
      </Reveal>
    </div>
  </section>
);

export const PageHero = ({ eyebrow, children, sub, right }) => (
  <section className="section-light relative overflow-hidden border-b border-slate-200 bg-[linear-gradient(180deg,#f3f5fa,#ffffff)] pt-[170px] md:pt-[196px]">
    <div className="stars-bg pointer-events-none absolute inset-y-0 right-0 w-1/2 opacity-60 [mask-image:linear-gradient(to_left,#000,transparent)]" />
    <div className="relative mx-auto grid max-w-[1400px] gap-10 px-5 pb-16 md:grid-cols-12 md:px-10 md:pb-20">
      <div className="md:col-span-7">
        <p className="font-mono text-xs uppercase tracking-[0.22em] text-[#D4A24C]">{eyebrow}</p>
        <h1 className="font-display mt-5 text-5xl font-bold uppercase leading-[0.9] sm:text-6xl lg:text-[5.5rem]">{children}</h1>
        {sub && <p className="mt-6 max-w-xl text-base text-slate-300 md:text-lg">{sub}</p>}
      </div>
      {right && <div className="md:col-span-5 md:self-end">{right}</div>}
    </div>
  </section>
);

const TONES = { light: "section-light", soft: "section-light section-soft", dark: "section-dark" };

export const Container = ({ children, className = "", id, testId, tone = "light" }) => (
  <div className={TONES[tone]}>
    <section id={id} data-testid={testId} className={`mx-auto max-w-[1400px] px-5 py-20 md:px-10 md:py-28 ${className}`}>{children}</section>
  </div>
);
